"use client";
import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

const KEY = "aluume_sentry_sid";

/**
 * Give Sentry an ANONYMOUS per-session id, so "users affected" is a real number.
 *
 * Every issue used to report "users affected: 0" — the first figure you read when triaging, and
 * it made every error look like it hit nobody. Release 220 tried to fix that with the staff auth
 * id, which was wrong twice over: `beforeSend` deleted it (dead code), and it only ever mounted
 * on the authenticated staff layout — so it could never have helped the ~80% of errors that come
 * from ANONYMOUS guests on the booking site, which is exactly where the volume is.
 *
 * WHY NOT THE IP: an IP is explicitly personal data under the NZ Privacy Act / GDPR — MORE
 * identifying than this, not less — and `sendDefaultPii: false` exists precisely to stop Sentry
 * attaching one. It would also be useless here: every device at the venue NATs to a single
 * address, so the whole team would collapse into one "user" no matter how many people hit a bug.
 *
 * WHAT THIS IS: a random UUID with no meaning anywhere — not derived from anything, stored in
 * NOTHING but this browser's sessionStorage, and gone when the tab closes. It cannot be resolved
 * to a person, an account, an email or a device, and it deliberately does NOT persist across
 * sessions so it can't act as a tracking id. It answers one question only: "was this one person
 * hitting refresh, or fifty people?"
 *
 * sessionStorage throws outright in some privacy modes, so every access is guarded — failing to
 * set an id must never break the page it is monitoring.
 */
export function SentryAnonSession({ role }: { role?: string }) {
  useEffect(() => {
    let sid: string | null = null;
    try {
      sid = sessionStorage.getItem(KEY);
      if (!sid) {
        sid =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `sid_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
        sessionStorage.setItem(KEY, sid);
      }
    } catch {
      // Private mode / storage blocked — carry on without an id rather than throw.
      return;
    }
    Sentry.setUser({ id: sid });
    // Role is a coarse bucket (owner/manager/staff), never an identity. Tags survive the scrub.
    if (role) Sentry.setTag("staff_role", role);
    return () => Sentry.setUser(null);
  }, [role]);
  return null;
}
